import { useRef, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
function HTMLSquarePreview({ html }) {
  return (
    <div className="w-full h-24 overflow-hidden bg-white border rounded-xl shadow-inner">
      <div
        style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
        dangerouslySetInnerHTML={{ __html: html }}
        className="p-2 text-xs"
      />
    </div>
  );
}

export default function MessageDrawer({ isOpen, onClose }) {
  const { broadcastId } = useParams();
  const drawerRef = useRef(null);
  const associatedDrawerRef = useRef(null);

  const [showAssociated, setShowAssociated] = useState(false);
  const [messages, setMessages] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [rawSubjectVariants, setRawSubjectVariants] = useState('');
  const [messageData, setMessageData] = useState({
    subject: '',
    subject_variants: [],
    text_content: '',
    html_content: '',
    images: [],
    from_name: '',
    from_email: '',
  });
  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    setEditingMessageId(null);
    setMessageData({
      subject: '',
      subject_variants: [],
      text_content: '',
      html_content: '',
      images: [],
      from_name: '',
      from_email: '',
    });
    setUploadedImages([]);
  }, [broadcastId]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target) &&
        (!associatedDrawerRef.current ||
          !associatedDrawerRef.current.contains(event.target))
      ) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Messages
  const fetchMessages = useCallback(async () => {
    const token = localStorage.getItem('prae.jwt');
    if (!broadcastId) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}/messages`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!response.ok) return setMessages([]);
      const data = await response.json();
      setMessages(data);
    } catch (e) {
      console.error('Erreur lors de la récupération des messages', e);
    }
  }, [broadcastId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleSelectMessage = message => {
    setEditingMessageId(message.id);
    setMessageData({
      subject: message.subject || '',
      subject_variants: message.subject_variants || [],
      text_content: message.text_content || '',
      html_content: message.html_content || '',
      images: message.images || [],
      from_name: message.from_name || '',
      from_email: message.from_email || '',
    });
    setRawSubjectVariants((message.subject_variants || []).join(';\n'));
    setUploadedImages(message.images || []);
    setShowAssociated(false);
  };

  const handleNewMessage = () => {
    setEditingMessageId(null);
    setMessageData({
      subject: '',
      subject_variants: [],
      text_content: '',
      html_content: '',
      images: [],
      from_name: '',
      from_email: '',
    });
    setUploadedImages([]);
  };

  const onDrop = useCallback(async acceptedFiles => {
    const token = localStorage.getItem('prae.jwt');
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('image', file);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/upload`,
          {
            method: 'POST',
            body: formData,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        if (!response.ok) throw new Error('Erreur upload');
        const data = await response.json();
        setUploadedImages(prev => [...prev, data.url]);
        setMessageData(prev => ({
          ...prev,
          images: [...prev.images, data.url],
        }));
      } catch (error) {
        toast.error("Erreur lors de l'upload de l'image.");
      }
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*',
  });

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'subject_variants') {
      setRawSubjectVariants(value);
      const variants = value
        .split(';')
        .map(s => s.trim())
        .filter(Boolean);
      return setMessageData(prev => ({ ...prev, subject_variants: variants }));
    }
    setMessageData(prev => ({ ...prev, [name]: value }));
  };

  const insertImageUrl = url => {
    setMessageData(prev => ({
      ...prev,
      html_content: prev.html_content + `<img src="${url}" alt="" />\n`,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const token = localStorage.getItem('prae.jwt');
    try {
      const url = editingMessageId
        ? `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}/messages/${editingMessageId}`
        : `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}/messages`;
      const method = editingMessageId ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...messageData,
          images: uploadedImages,
        }),
      });
      if (!response.ok) throw new Error('Erreur sauvegarde');
      await fetchMessages();
      toast.success(
        editingMessageId
          ? 'Modifications enregistrées !'
          : 'Message créé avec succès !'
      );
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde.');
    }
  };

  const handleDeleteMessage = async id => {
    const token = localStorage.getItem('prae.jwt');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastId}/messages/${id}`,
        {
          method: 'DELETE',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      if (!response.ok) throw new Error('Erreur suppression');
      await fetchMessages();
      if (editingMessageId === id) handleNewMessage();
      toast.info('Message supprimé.');
    } catch (e) {
      toast.error('Erreur lors de la suppression.');
    }
  };

  const drawerWidthClass = showAssociated
    ? 'w-[100vw] max-w-8xl'
    : 'w-[130vw] max-w-10xl';

  return (
    <>
      <div
        className={`fixed top-14 inset-0 bg-black bg-opacity-30 transition-opacity z-40 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        } flex items-stretch`}
        style={{ top: '59px', bottom: 0, zIndex: 2000 }}
      >
        <ToastContainer
          position="top-center"
          autoClose={2000}
          hideProgressBar
        />

        <div
          ref={drawerRef}
          className={`h-full overflow-hidden  flex flex-col transition-all duration-300 bg-[#f7f8fa] ${drawerWidthClass} `}
        >
          <div className="flex justify-between items-center border-b px-6 py-2 bg-white rounded-tr-xl">
            <div className="font-semibold text-xl text-gray-800 tracking-tight flex items-center gap-3">
              {editingMessageId ? 'Éditer un message' : 'Nouveau message'}
            </div>
            <div className="flex gap-3">
              <button
                className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-black-200 rounded-xl px-4 py-2 text-sm shadow-sm transition"
                onClick={() => setShowAssociated(true)}
              >
                Messages associés
              </button>
              <button
                onClick={onClose}
                className="px-3 py-2 rounded-xl text-lg font-bold bg-white border border-gray-200 hover:bg-gray-100 text-gray-600 transition"
                title="Fermer"
              >
                ×
              </button>
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            <div className="w-1/2 h-full px-8 py-7 overflow-y-auto flex flex-col gap-8">
              <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Nom expéditeur
                    </label>
                    <input
                      type="text"
                      name="from_name"
                      value={messageData.from_name}
                      onChange={handleChange}
                      className="w-full p-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="Exemple : Pierre Dupont"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 font-medium text-gray-700">
                      Email expéditeur
                    </label>
                    <input
                      type="email"
                      name="from_email"
                      value={messageData.from_email}
                      onChange={handleChange}
                      className="w-full p-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                      placeholder="exemple@votre-domaine.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Sujet
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={messageData.subject}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Variantes de sujet (une variante par ligne ou séparée par ;)
                  </label>
                  <textarea
                    name="subject_variants"
                    value={rawSubjectVariants}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    rows={4}
                    placeholder={`Exemple :\nSujet variante 1;\nSujet variante 2;\nSujet variante 3`}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Contenu Texte
                  </label>
                  <textarea
                    name="text_content"
                    value={messageData.text_content}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Contenu HTML
                  </label>
                  <textarea
                    name="html_content"
                    value={messageData.html_content}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-200 rounded-lg font-mono bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    rows={7}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Images
                  </label>
                  <div
                    {...getRootProps()}
                    className="border-dashed border-2 border-gray-200 p-3 text-center rounded-xl bg-gray-50 cursor-pointer hover:bg-gray-100 transition"
                  >
                    <input {...getInputProps()} />
                    {isDragActive
                      ? 'Déposez les images ici…'
                      : 'Glissez-déposez ou cliquez pour choisir des images'}
                  </div>
                  {uploadedImages.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-3">
                      {uploadedImages.map((url, i) => (
                        <li
                          key={i}
                          className="flex flex-col items-center gap-1"
                        >
                          <img
                            src={url}
                            alt=""
                            className="w-14 h-14 object-cover rounded-lg shadow border"
                          />
                          <span className="text-[0.65rem] break-all text-gray-400">
                            {url}
                          </span>
                          <button
                            type="button"
                            onClick={() => insertImageUrl(url)}
                            className="text-xs bg-blue-100 px-2 py-0.5 rounded-lg text-blue-700 hover:bg-blue-200"
                          >
                            Insérer dans HTML
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="flex gap-2 justify-end pt-1">
                  <button
                    type="button"
                    className="bg-gray-100 hover:bg-gray-200 rounded-xl px-5 py-2 text-gray-700 font-medium transition"
                    onClick={handleNewMessage}
                  >
                    Nouveau
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-8 py-2 rounded-xl font-semibold shadow-sm hover:bg-blue-700 transition"
                  >
                    {editingMessageId ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>

            <div className="w-1/2 h-full px-8 py-7 overflow-y-auto bg-[#f7f8fa] flex flex-col gap-4">
              <div className="font-bold text-base text-gray-700 mb-1">
                Prévisualisation HTML (live)
              </div>
              <div className="border rounded-xl bg-white p-6 min-h-[180px] max-w-full overflow-auto shadow-sm transition">
                <div
                  dangerouslySetInnerHTML={{ __html: messageData.html_content }}
                />
              </div>
            </div>
          </div>
        </div>

        {showAssociated && (
          <div
            ref={associatedDrawerRef}
            className="fixed right-0 top-0 h-full w-[40vw] max-w-md bg-white shadow-2xl border-l z-50 flex flex-col rounded-l-xl"
            style={{ zIndex: 3000 }}
          >
            <div className="flex justify-between items-center p-4 border-b bg-white rounded-tr-xl">
              <div className="font-bold text-base text-gray-700">
                Messages associés
              </div>
              <button
                className="text-2xl px-3 py-1 bg-gray-100 rounded hover:bg-gray-200"
                onClick={() => setShowAssociated(false)}
                title="Fermer"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 gap-3">
              {messages.length === 0 && (
                <div className="col-span-2 text-center text-gray-400 italic">
                  Aucun message
                </div>
              )}
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`border rounded-xl p-2 shadow hover:shadow-md cursor-pointer group relative bg-white transition ${
                    editingMessageId === message.id
                      ? 'ring-2 ring-blue-300'
                      : ''
                  }`}
                  title="Cliquer pour éditer ce message"
                  onClick={() => handleSelectMessage(message)}
                >
                  <button
                    className="absolute top-2 right-2 text-red-500 opacity-70 hover:opacity-100 z-10"
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteMessage(message.id);
                    }}
                    title="Supprimer"
                  >
                    ✖
                  </button>
                  <div className="text-xs font-bold mb-1 truncate text-gray-700">
                    {message.subject}
                  </div>
                  <HTMLSquarePreview html={message.html_content} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
